/**
 * Table Handle Extension
 * 
 * ProseMirror plugin for managing table row/column handles
 * with drag-and-drop support for reordering.
 * Also adds keyboard shortcuts for deleting selected rows/columns.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
    TableMap,
    CellSelection,
    selectedRect,
    isInTable,
} from '@tiptap/pm/tables';
import { findTable, getCellsInColumn, getCellsInRow } from './table-utils';

export interface TableHandleState {
    tablePos: number | null;
    tableNode: any | null;
    tableMap: TableMap | null;
    hoveredRow: number | null;
    hoveredColumn: number | null;
    selectedRows: number[];
    selectedColumns: number[];
    isDragging: boolean;
    dragType: 'row' | 'column' | null;
    dragIndex: number | null;
    dropIndex: number | null;
}

const tableHandlePluginKey = new PluginKey<TableHandleState>('tableHandle');

export const TableHandleExtension = Extension.create({
    name: 'tableHandle',

    addKeyboardShortcuts() {
        return {
            // Delete selected row(s) with Backspace
            'Backspace': ({ editor }) => {
                const { selection } = editor.state;

                // Only handle if we're in a table with a cell selection
                if (selection instanceof CellSelection) {
                    const rect = selectedRect(editor.state);

                    // Check if entire row(s) are selected
                    const table = findTable(selection);
                    if (table) {
                        const map = TableMap.get(table.node);

                        // If selection spans full width, it's a row selection
                        if (rect.left === 0 && rect.right === map.width) {
                            editor.chain().focus().deleteRow().run();
                            return true;
                        }

                        // If selection spans full height, it's a column selection
                        if (rect.top === 0 && rect.bottom === map.height) {
                            editor.chain().focus().deleteColumn().run();
                            return true;
                        }
                    }
                }

                return false; // Let default behavior handle
            },

            // Also handle Delete key
            'Delete': ({ editor }) => {
                const { selection } = editor.state;

                if (selection instanceof CellSelection) {
                    const rect = selectedRect(editor.state);
                    const table = findTable(selection);

                    if (table) {
                        const map = TableMap.get(table.node);

                        if (rect.left === 0 && rect.right === map.width) {
                            editor.chain().focus().deleteRow().run();
                            return true;
                        }

                        if (rect.top === 0 && rect.bottom === map.height) {
                            editor.chain().focus().deleteColumn().run();
                            return true;
                        }
                    }
                }

                return false;
            },
        };
    },

    addProseMirrorPlugins() {
        const editor = this.editor;

        return [
            new Plugin<TableHandleState>({
                key: tableHandlePluginKey,
                state: {
                    init() {
                        return {
                            tablePos: null,
                            tableNode: null,
                            tableMap: null,
                            hoveredRow: null,
                            hoveredColumn: null,
                            selectedRows: [],
                            selectedColumns: [],
                            isDragging: false,
                            dragType: null,
                            dragIndex: null,
                            dropIndex: null,
                        };
                    },
                    apply(tr, prev) {
                        const meta = tr.getMeta(tableHandlePluginKey);
                        if (meta) {
                            return { ...prev, ...meta };
                        }
                        return prev;
                    },
                },
                props: {
                    decorations(state) {
                        const { tablePos, hoveredRow, hoveredColumn, isDragging, dropIndex, dragType } =
                            tableHandlePluginKey.getState(state) || {};

                        if (!tablePos || isDragging) return DecorationSet.empty;

                        const decorations: Decoration[] = [];

                        // Add hover highlight decorations if needed
                        // This can be extended for more visual feedback

                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },
});

export { tableHandlePluginKey };
export default TableHandleExtension;

